import { useCallback, useMemo, useRef, useState } from 'react';

import { DownloadIcon, Trash2Icon } from 'lucide-react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { DurationInput } from '@/components/organisms/DurationInput/DurationInput';
import { Button } from '@/components/ui/button';
import { Form, FormDescription, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useIntegrations } from '@/hooks/useIntegrations';
import { getPermissions } from '@/redux/permissions/selectors';
import { WhatsAppIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { decodeBase64, encodeBase64 } from '@/util/encoding';
import { getPemPublicKeyError } from '@/util/validation/pemPublicKey';

import { SESSION_TIMEOUT } from './constants';

type AdvancedForm = {
  session_timeout_enabled: boolean;
  session_timeout: number;
  encryption_enabled: boolean;
};

interface Props {
  integration: WhatsAppIntegration;
}

const FORM_ID = 'whatsapp-advanced-form';

const Advanced = ({ integration }: Props) => {
  const { t } = useTranslation();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );

  const { updateIntegration, updateStatus } = useIntegrations<WhatsAppIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );

  // Encryption state - only track user input, derive existing key from integration
  const [pasteError, setPasteError] = useState<string | null>(null);
  const [pastedKey, setPastedKey] = useState<string>('');
  const [keyRemoved, setKeyRemoved] = useState(false);
  const validationRequestRef = useRef(0);

  // Compute existing key content from integration (memoized)
  const existingKeyContent = useMemo(() => {
    const encodedKey = integration?.config?.public_customer_rsa_key;
    if (!encodedKey) {
      return null;
    }
    return decodeBase64(encodedKey);
  }, [integration?.config?.public_customer_rsa_key]);

  const hasExistingKey = !!existingKeyContent && !keyRemoved;

  const hasCustomSessionTimeout = !!integration?.config?.session_timeout;

  const form = useForm<AdvancedForm>({
    mode: 'onChange',
    defaultValues: {
      session_timeout_enabled: hasCustomSessionTimeout,
      // Server stores seconds, form uses seconds
      session_timeout: integration?.config?.session_timeout || SESSION_TIMEOUT.DEFAULT_SECONDS,
      encryption_enabled: !!existingKeyContent,
    },
  });

  const { control, handleSubmit, setValue } = form;

  const encryptionEnabled = useWatch({ control, name: 'encryption_enabled' });
  const sessionTimeoutEnabled = useWatch({ control, name: 'session_timeout_enabled' });
  const sessionTimeoutValue = useWatch({ control, name: 'session_timeout' });

  const handleSessionTimeoutToggle = useCallback(
    (enabled: boolean) => {
      setValue('session_timeout_enabled', enabled, { shouldDirty: true });
      if (enabled && !sessionTimeoutValue) {
        setValue('session_timeout', SESSION_TIMEOUT.DEFAULT_SECONDS);
      }
    },
    [setValue, sessionTimeoutValue]
  );

  // Derive the effective key content: pastedKey takes precedence over existing
  const effectiveKeyContent = pastedKey.trim() || (hasExistingKey ? existingKeyContent : null);

  const handlePasteChange = useCallback(
    async (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setPastedKey(value);

      if (!value.trim()) {
        setPasteError(null);
        return;
      }

      const requestId = validationRequestRef.current + 1;
      validationRequestRef.current = requestId;

      const errorKey = await getPemPublicKeyError(value);
      if (validationRequestRef.current !== requestId) {
        return;
      }

      setPasteError(errorKey ? t(errorKey) : null);
    },
    [t]
  );

  const handleEncryptionToggle = useCallback(
    (enabled: boolean) => {
      setValue('encryption_enabled', enabled, { shouldDirty: true });
      if (!enabled) {
        setPastedKey('');
        setPasteError(null);
        setKeyRemoved(true);
      } else {
        setKeyRemoved(false);
      }
    },
    [setValue]
  );

  const handleRemoveKey = useCallback(() => {
    setPastedKey('');
    setPasteError(null);
    setKeyRemoved(true);
  }, []);

  const handleDownloadKey = useCallback(() => {
    if (!effectiveKeyContent) {
      return;
    }

    const blob = new Blob([effectiveKeyContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'public_key.pem';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [effectiveKeyContent]);

  const onSubmit = async (values: AdvancedForm) => {
    if (values.encryption_enabled && pastedKey.trim()) {
      const errorKey = await getPemPublicKeyError(pastedKey);
      if (errorKey) {
        setPasteError(t(errorKey));
        return;
      }
    }

    const updatedConfig = { ...integration.config };

    // Server expects seconds
    if (values.session_timeout_enabled && values.session_timeout > 0) {
      updatedConfig.session_timeout = values.session_timeout;
    } else {
      delete updatedConfig.session_timeout;
    }

    // Encryption
    updatedConfig.public_customer_rsa_key =
      values.encryption_enabled && effectiveKeyContent
        ? encodeBase64(effectiveKeyContent)
        : undefined;

    updateIntegration({ ...integration, config: updatedConfig });

    // Reset local state after successful submit
    setPastedKey('');
    setKeyRemoved(false);
  };

  // Calculate dirty state
  const serverHasCustomSessionTimeout = !!integration?.config?.session_timeout;
  const serverSessionTimeout = integration?.config?.session_timeout ?? 0;

  const isSessionTimeoutDirty =
    sessionTimeoutEnabled !== serverHasCustomSessionTimeout ||
    (sessionTimeoutEnabled && sessionTimeoutValue !== serverSessionTimeout);

  const serverHasKey = !!existingKeyContent;
  const isEncryptionDirty =
    encryptionEnabled !== serverHasKey ||
    (encryptionEnabled && effectiveKeyContent !== existingKeyContent) ||
    (!encryptionEnabled && serverHasKey) ||
    keyRemoved;

  const isDirty = isSessionTimeoutDirty || isEncryptionDirty;
  const isSaveDisabled =
    !isDirty || !canWrite || (encryptionEnabled && !effectiveKeyContent) || !!pasteError;
  const hasStoredKey = hasExistingKey && !pastedKey.trim();

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
        <div id="whatsapp-advanced-section" className="w-full">
          <div className="mb-2 flex items-center justify-between">
            <p className="px-1.5 text-sm leading-none font-medium text-foreground-muted">
              {t('integrations.whatsapp.advanced.title')}
            </p>
          </div>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={updateStatus === 'pending'}
            disabled={isSaveDisabled}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <div className="flex flex-col gap-6">
              {/* Session Timeout Section */}
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">{t('integrations.session_timeout.title')}</p>
                    <p className="text-sm text-foreground-muted">
                      {t('integrations.session_timeout.subtitle')}
                    </p>
                  </div>
                  <Controller
                    name="session_timeout_enabled"
                    control={control}
                    render={({ field: { value } }) => (
                      <Switch
                        checked={value}
                        onCheckedChange={handleSessionTimeoutToggle}
                        disabled={!canWrite}
                      />
                    )}
                  />
                </div>
                {sessionTimeoutEnabled && (
                  <Controller
                    name="session_timeout"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <DurationInput
                        value={value || SESSION_TIMEOUT.DEFAULT_SECONDS}
                        onChange={onChange}
                        disabled={!canWrite}
                        minSeconds={SESSION_TIMEOUT.MIN_SECONDS}
                        maxSeconds={SESSION_TIMEOUT.MAX_SECONDS}
                        className="mt-0"
                      />
                    )}
                  />
                )}
              </div>

              {/* Encryption Section */}
              <div id="whatsapp-encryption-section" className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium">
                      {t('integrations.security.checkbox.title')}
                    </p>
                    <p className="text-sm text-foreground-muted">
                      {t('integrations.security.checkbox.subtitle', { 0: 'WhatsApp' })}
                    </p>
                  </div>
                  <Controller
                    name="encryption_enabled"
                    control={control}
                    render={({ field: { value } }) => (
                      <Switch
                        checked={value}
                        onCheckedChange={handleEncryptionToggle}
                        disabled={!canWrite}
                      />
                    )}
                  />
                </div>

                {encryptionEnabled && (
                  <FormItem>
                    <FormLabel>{t('integrations.security.input_label')}</FormLabel>

                    {hasStoredKey && existingKeyContent ? (
                      <div className="flex items-center justify-between rounded-lg border bg-muted p-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">public_key.pem</p>
                          <p className="text-xs text-foreground-muted">
                            {t('integrations.security.key_configured')}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <CopyButton data={existingKeyContent} iconSize={16} />
                          <Button
                            type="button"
                            variant="ghost-neutral"
                            size="icon-sm"
                            onClick={handleDownloadKey}
                            aria-label={t('common.download')}
                          >
                            <DownloadIcon className="h-4 w-4" />
                          </Button>
                          {canWrite && (
                            <Button
                              className="hover:text-destructive hover:[&_svg]:text-destructive"
                              type="button"
                              variant="ghost-neutral"
                              size="icon-sm"
                              onClick={handleRemoveKey}
                              aria-label={t('common.remove')}
                            >
                              <Trash2Icon className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <>
                        <Textarea
                          value={pastedKey}
                          onChange={handlePasteChange}
                          placeholder={t('integrations.security.placeholder')}
                          disabled={!canWrite}
                          className="min-h-32 font-mono text-sm"
                        />
                        {pasteError && <FormMessage>{pasteError}</FormMessage>}
                      </>
                    )}

                    <FormDescription>{t('integrations.security.tooltip')}</FormDescription>
                  </FormItem>
                )}
              </div>
            </div>
          </FieldsetCard>
        </div>
      </form>
    </Form>
  );
};

export default Advanced;
